// ⚠️ Toasts MUST NOT auto-dismiss. Do not add a setTimeout, a fade-out, or a
// "duration" prop to this file - not even a long one. Errors here are often the
// only record of a failure, and they need to stay on screen long enough to be
// read, selected, copied, and screenshotted. A toast leaves the screen only when
// the user clicks its X or "Dismiss all". This has been re-broken once already
// (a 5s timer reappeared in 0d06496); if you are here to "clean up" the missing
// timeout, don't.
import { usePage } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'

interface ToastItem {
  id: string
  type: 'alert' | 'notice'
  message: string
}

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: string) => void }) {
  const isAlert = item.type === 'alert'

  return (
    <div
      role="alert"
      className={`pointer-events-auto shrink-0 flex items-center gap-4 p-4 rounded-xl shadow-2xl border w-105 ${
        isAlert ? 'bg-red-950 border-red-900' : 'bg-green-950 border-green-900'
      }`}
    >
      <div className={`flex items-center justify-center w-8 h-8 rounded-md shrink-0 text-white ${isAlert ? 'bg-red-600' : 'bg-green-500'}`}>
        {isAlert ? (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className="flex-1 font-semibold text-[15px] text-white select-text break-words">{item.message}</span>
      <button
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss"
        className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
      >
        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

export function FlashMessage() {
  const { flash } = usePage().props as any
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const prev = useRef<{ alert?: string; notice?: string; error?: string; success?: string }>({})

  useEffect(() => {
    const incoming: ToastItem[] = []
    if (flash?.alert && flash.alert !== prev.current.alert) {
      incoming.push({ id: crypto.randomUUID(), type: 'alert', message: flash.alert })
    }
    if (flash?.error && flash.error !== prev.current.error) {
      incoming.push({ id: crypto.randomUUID(), type: 'alert', message: flash.error })
    }
    if (flash?.notice && flash.notice !== prev.current.notice) {
      incoming.push({ id: crypto.randomUUID(), type: 'notice', message: flash.notice })
    }
    if (flash?.success && flash.success !== prev.current.success) {
      incoming.push({ id: crypto.randomUUID(), type: 'notice', message: flash.success })
    }
    if (incoming.length > 0) {
      prev.current = { alert: flash?.alert, notice: flash?.notice, error: flash?.error, success: flash?.success }
      setToasts(t => [...t, ...incoming])
    }
  }, [flash?.alert, flash?.notice, flash?.error, flash?.success])

  const dismiss = (id: string) => setToasts(t => t.filter(x => x.id !== id))
  const dismissAll = () => setToasts([])

  if (toasts.length === 0) return null

  // Toasts NEVER auto-dismiss - see the note at the top of this file.
  // They persist until the user clicks X or Dismiss all, so the stack is
  // capped to the viewport and scrolls instead of running off the screen.
  return (
    <div className="fixed top-6 right-6 z-50 flex flex-col items-end gap-2 pointer-events-none max-h-[calc(100vh-3rem)] overflow-y-auto">
      {toasts.length > 1 && (
        <button
          onClick={dismissAll}
          className="pointer-events-auto shrink-0 px-3 py-1 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-xs font-semibold opacity-80 hover:opacity-100 transition-opacity"
        >
          Dismiss all ({toasts.length})
        </button>
      )}
      {toasts.map(t => <Toast key={t.id} item={t} onDismiss={dismiss} />)}
    </div>
  )
}
